import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { pascalCase } from 'change-case';
import { useParams } from 'react-router';
import CloudUpload from '@material-ui/icons/CloudUpload';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import Close from '@material-ui/icons/Close';
import { TextInput } from '../../../../atoms';
import Dropdown from './Dropdown';
import { RootState } from '../../../../redux/root';
import { dispatch } from '../../../../redux/store';
import { setPermittingDoc, setPermittingDocErrors } from '../../../../redux/slices/wikiSlice';
import { PermittingDocumentType } from '../../../../api/jurisdiction';
import { useSaveWikiPermittingDocumentMutation, useUpdateWikiPermittingDocumentMutation } from '../../../../redux/rtkQuery/apiWikiPermittingSlice';

const AddPermittingDocumentPopup = ({ setShowAddDocument }) => {
  const { permittingDoc: data, permittingDocErrors: errors } = useSelector((s: RootState) => s.wikiSlice);
  const [file, setFile] = useState<File | undefined>();
  const [showFileOnEditing, setShowFileOnEditing] = useState<boolean>(false);
  const [dragging, setDragging] = useState<boolean>(false);
  const [fileTypeError, setFileTypeError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>();
  // @ts-ignore
  const { wikiId } = useParams();
  const [saveWikiPermittingDocument] = useSaveWikiPermittingDocumentMutation();
  const [updateWikiPermittingDocument] = useUpdateWikiPermittingDocumentMutation();

  useEffect(() => {
    if (!data.isEditing) {
      dispatch(setPermittingDoc({} as PermittingDocumentType));
    } else {
      setShowFileOnEditing(true);
    }
  }, [data.isEditing]);
  useEffect(() => {
    dispatch(setPermittingDocErrors({}));
    return () => {
      dispatch(
        setPermittingDoc({} as PermittingDocumentType),
      );
    };
  }, []);
  const isPdfFile = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');

  const consumeFile = (selectedFile?: File) => {
    if (!selectedFile) return;
    if (!isPdfFile(selectedFile)) {
      setFileTypeError('Only PDF files are allowed.');
      return;
    }
    setFileTypeError('');
    setShowFileOnEditing(false);
    setFile(selectedFile);
    dispatch(
      setPermittingDoc({
        ...data,
        fileName: selectedFile.name,
      }),
    );
  };

  const handleFileChange = e => {
    consumeFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = e => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setDragging(false);
    consumeFile(e.dataTransfer.files?.[0]);
  };
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };
  const clearFile = e => {
    e.stopPropagation();
    setFile(undefined);
    setShowFileOnEditing(false);
    dispatch(setPermittingDoc({ ...data, fileName: '' }));
  };

  const onSubmit = async () => {
    const keys = ['work', 'building', 'permitType', 'documentType', 'documentName'];
    let errorsFound = false;
    const _errors = {};
    keys.forEach(k => {
      if (!data[k]) {
        errorsFound = true;
        _errors[k] = {
          message: `${pascalCase(k)} is required!`,
        };
      }
    });
    if (errorsFound) {
      dispatch(setPermittingDocErrors(_errors));
      return;
    }

    const formData = new FormData();
    formData.append('jurisdiction', wikiId);
    if (file) {
      formData.append('permitting_report', file);
      formData.append('permitting_visible_filename', data.fileName);
    }
    formData.append('wiki_document_type', data.documentType.value);
    formData.append('work', data.work.value);
    formData.append('building', data.building.value);
    formData.append('permit_type', data.permitType.value);
    formData.append('document_name', data.documentName);

    if (data.isEditing) {
      const _frm = {
        id: data.id,
        frm: formData,
      };
      formData.append('id', `${data.id}`);
      await updateWikiPermittingDocument(_frm);
    } else {
      await saveWikiPermittingDocument(formData);
    }

    setShowAddDocument(false);
    const _permittingDoc = {
      building: undefined,
      documentName: '',
      documentType: undefined,
      fileName: '',
      permitType: undefined,
      work: undefined,
    };
    dispatch(setPermittingDoc(_permittingDoc));
  };

  const onChange = (e, name) => {
    dispatch(
      setPermittingDoc({
        ...data,
        [name]: e,
      }),
    );
    const _errors = { ...errors };
    delete _errors[name];
    dispatch(setPermittingDocErrors(_errors));
  };

  const onDocNameChange = e => {
    dispatch(setPermittingDoc({ ...data, documentName: e.target.value }));
    const _errors = { ...errors };
    delete _errors.documentName;
    dispatch(setPermittingDocErrors(_errors));
  };
  const displayFileName = file?.name || (showFileOnEditing ? data.fileName : '') || '';

  return (
    <Form>
      <Dropdown
        showValidation
        label="Work"
        entity={2}
        name="work"
        value={data ? data.work : {}}
        onChange={e => onChange(e, 'work')}
      />
      <Dropdown
        showValidation
        label="Building"
        entity={1}
        name="building"
        value={data ? data.building : {}}
        onChange={e => onChange(e, 'building')}
      />
      <Dropdown
        showValidation
        label="Permit Type"
        entity={14}
        name="permitType"
        value={data ? data.permitType : {}}
        onChange={e => onChange(e, 'permitType')}
      />
      <Dropdown
        showValidation
        label="Document Type"
        entity={13}
        name="documentType"
        value={data ? data.documentType : {}}
        onChange={e => onChange(e, 'documentType')}
      />

      <FieldGroup>
        <FieldLabel>
          Document Name<RedStar>*</RedStar>
        </FieldLabel>
        <TextInput label="" value={data.documentName} required onChange={onDocNameChange} error={errors && errors.documentName} />
        {errors && errors.documentName && <FieldError>{errors.documentName.message}</FieldError>}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>File</FieldLabel>
        <DropZone
          active={dragging}
          onClick={openFilePicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') openFilePicker(); }}
        >
          <CloudUpload />
          <DropText>
            <DropPrimary>{dragging ? 'Drop file to upload' : 'Drag & drop or click to upload'}</DropPrimary>
            <DropSecondary>PDF document</DropSecondary>
          </DropText>
          {displayFileName && (
            <FilePill onClick={e => e.stopPropagation()}>
              <InsertDriveFile fontSize="small" />
              <span>{displayFileName}</span>
              <ClearButton type="button" onClick={clearFile} title="Remove file">
                <Close fontSize="small" />
              </ClearButton>
            </FilePill>
          )}
          <HiddenInput ref={fileInputRef} onChange={handleFileChange} accept="application/pdf,.pdf" type="file" />
        </DropZone>
        {fileTypeError && <FieldError>{fileTypeError}</FieldError>}
      </FieldGroup>

      <Actions>
        <SecondaryButton type="button" onClick={() => setShowAddDocument(false)}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onSubmit}>
          {data.isEditing ? 'Update' : 'Save'}
        </PrimaryButton>
      </Actions>
    </Form>
  );
};

export default AddPermittingDocumentPopup;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 420px;
  font-family: ${FONT_STACK};
  .MuiOutlinedInput-root {
    height: 40px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const FieldError = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const DropZone = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 16px;
  border: 2px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  cursor: pointer;
  text-align: center;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;

  svg {
    font-size: 2rem;
    color: var(--neutral-400);
    transition: color 0.15s ease;
  }

  &:hover {
    border-color: var(--primary-300);
    background: var(--primary-50);

    svg {
      color: var(--primary-600);
    }
  }

  ${({ active }) =>
    active &&
    css`
      border-color: var(--primary-600);
      background: var(--primary-50);

      svg {
        color: var(--primary-600);
      }
    `}
`;

const DropText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DropPrimary = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const DropSecondary = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const FilePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 6px 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--primary-600);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  max-width: 100%;

  svg {
    font-size: 1rem;
    color: var(--primary-600);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 240px;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--neutral-100);
    color: var(--danger-500);
  }

  svg {
    font-size: 0.95rem !important;
    color: inherit !important;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecondaryButton = styled.button`
  min-width: 100px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

// Backward-compat exports — other popups (Add Note, etc.) import these.
export const PopupWrapper = Form;
export const TextInputWrapper = FieldGroup;
export const PopupLabel = FieldLabel;
export const PopupError = FieldError;

export const RedStar = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;
